import { useDevicesList, whenever } from "@vueuse/core";
import { computed, nextTick, ref, watch } from "vue";
import { MediaSource } from "../types/";
export const useMediaStream = (config) => {
    let audioStream;
    const videoStream = ref();
    let desktopStream;
    let canvasStream;
    const canvasEl = config.canvasEl;
    const mediaStream = config?.mediaStream || ref();
    const audioInputDeviceId = config?.audioInputDeviceId || ref();
    const videoInputDeviceId = config?.videoInputDeviceId || ref();
    const source = config?.source || MediaSource.Audio;
    const muted = config?.muted || ref(false);
    const cameraDisabled = config?.cameraDisabled || ref(false);
    const devices = config?.devices || ref();
    const audioInputs = computed(() => devices.value?.filter((a) => a.kind == "audioinput" && a.deviceId != "") || []);
    const videoInputs = computed(() => devices.value?.filter((a) => a.kind == "videoinput" && a.deviceId != "") || []);
    const init = async () => {
        console.log("media-stream init");
        if (audioInputs.value[0]) {
            audioInputDeviceId.value = audioInputs.value[0]?.deviceId;
            await getAudioStream(audioInputDeviceId.value);
        }
        if (source == MediaSource.Video) {
            if (videoInputs?.value[0]) {
                videoInputDeviceId.value = videoInputs.value[0].deviceId;
                await buildVideoStream(videoInputDeviceId.value);
            }
        }
        rebuildStream();
    };
    const closeStreams = () => {
        desktopStream?.getTracks().forEach((t) => t.stop());
        videoStream.value?.getTracks().forEach((t) => t.stop());
        audioStream?.getTracks().forEach((t) => t.stop());
        mediaStream.value?.getTracks().forEach((t) => t.stop());
    };
    const inputDevicesAvailable = computed(() => {
        return audioInputs.value.length > 0 || (videoInputs.value.length > 0 && source != MediaSource.Audio);
    });
    whenever(inputDevicesAvailable, init);
    const loadDevices = () => {
        devices.value = useDevicesList({ requestPermissions: true }).devices.value;
        if (devices.value?.length <= 0) {
            // useDevicesList doesn't always work Firefox requires a pull from the media service before listing devices.
            Promise.allSettled(source == MediaSource.Video
                ? [navigator.mediaDevices.getUserMedia({ audio: true, video: true })]
                : [navigator.mediaDevices.getUserMedia({ audio: true })]).then(async (value) => {
                value.forEach((s) => {
                    if (s.status === "fulfilled") {
                        s.value.getTracks().forEach((t) => t.stop());
                    }
                });
                devices.value = await navigator.mediaDevices.enumerateDevices();
            });
        }
    };
    if (!inputDevicesAvailable.value) {
        loadDevices();
    }
    navigator.permissions
        .query(
    // @ts-expect-error - Missing 'microphone' option
    { name: "microphone" })
        .then(function (permissionStatus) {
        permissionStatus.onchange = function () {
            loadDevices();
        };
    })
        .catch();
    navigator.permissions
        .query(
    // @ts-expect-error - Missing 'camera' option
    { name: "camera" })
        .then(function (permissionStatus) {
        permissionStatus.onchange = function () {
            loadDevices();
        };
    })
        .catch();
    const audioInputDevice = computed(() => audioInputs.value.find((device) => audioInputDeviceId.value == device.deviceId));
    const videoInputDevice = computed(() => videoInputs.value.find((device) => videoInputDeviceId.value == device.deviceId));
    const getMediaStream = async (constraints) => {
        return await navigator.mediaDevices.getUserMedia(constraints);
    };
    watch(muted, () => {
        audioStream?.getAudioTracks().forEach((track) => {
            track.enabled = !muted.value;
        });
    });
    watch(cameraDisabled, () => {
        mediaStream.value?.getVideoTracks().forEach((track) => {
            track.enabled = !cameraDisabled.value;
        });
    });
    const rebuildStream = () => {
        const tracks = source == MediaSource.Screen
            ? [...(desktopStream?.getVideoTracks() ?? []), ...mergeAudioStreams()]
            : [
                ...((canvasStream ? canvasStream.getVideoTracks() : videoStream.value?.getVideoTracks()) ?? []),
                ...(audioStream?.getAudioTracks() ?? [])
            ];
        mediaStream.value = new MediaStream(tracks);
    };
    const getAudioStream = async (audioInputDeviceId) => {
        if (!audioInputDeviceId)
            return undefined;
        audioStream = await getMediaStream({
            audio: {
                deviceId: audioInputDeviceId ? { exact: audioInputDeviceId } : undefined,
                channelCount: 2,
                autoGainControl: true,
                echoCancellation: source != MediaSource.Screen,
                noiseSuppression: true
            }
        });
        audioStream.getAudioTracks().forEach((track) => {
            track.enabled = !muted.value;
        });
        return audioStream;
    };
    const buildVideoStream = async (videoInputDeviceId) => {
        if (!videoInputDeviceId)
            return undefined;
        if (canvasEl?.value && !canvasStream) {
            canvasStream = canvasEl.value.captureStream();
        }
        videoStream.value = await getMediaStream({
            video: { deviceId: videoInputDeviceId ? { exact: videoInputDeviceId } : undefined }
        });
        (canvasStream ?? videoStream.value).getVideoTracks().forEach((track) => {
            track.enabled = !cameraDisabled.value;
        });
        return videoStream.value;
    };
    const getDesktopStream = async (displaySurface) => {
        const stream = await navigator.mediaDevices.getDisplayMedia({
            video: displaySurface ? { displaySurface: displaySurface } : true,
            audio: {
                channelCount: 2,
                autoGainControl: false,
                echoCancellation: false,
                noiseSuppression: false
            }
        });
        resetDesktopStream();
        desktopStream = stream;
        rebuildStream();
    };
    const resetDesktopStream = async () => {
        desktopStream?.getTracks().forEach((t) => t.stop());
    };
    watch(canvasEl, async () => {
        console.log("canvasEl changed", canvasEl);
        canvasStream = canvasEl?.value?.captureStream();
        rebuildStream();
    });
    watch(audioInputDevice, async (newAudio, oldAudio) => {
        if (mediaStream.value && newAudio && newAudio.deviceId != oldAudio?.deviceId) {
            if (newAudio?.deviceId) {
                await getAudioStream(newAudio.deviceId);
            }
            rebuildStream();
        }
    });
    watch(videoInputDevice, async (newVideo, oldVideo) => {
        if (mediaStream.value && newVideo && newVideo.deviceId != oldVideo?.deviceId) {
            await buildVideoStream(newVideo.deviceId);
            rebuildStream();
        }
    });
    // Kickstart the stream
    const resetStream = () => {
        const stream = mediaStream.value;
        mediaStream.value = undefined;
        nextTick(() => {
            mediaStream.value = stream;
        });
    };
    const mergeAudioStreams = () => {
        if (!desktopStream || desktopStream?.getAudioTracks().length == 0) {
            return audioStream?.getAudioTracks() ?? [];
        }
        else if (!audioStream || audioStream?.getAudioTracks().length == 0) {
            return desktopStream?.getAudioTracks() ?? [];
        }
        const context = new AudioContext();
        const streamA = context.createMediaStreamSource(desktopStream);
        const streamB = context.createMediaStreamSource(audioStream);
        const merged = context.createMediaStreamDestination();
        const gainA = context.createGain();
        const gainB = context.createGain();
        gainA.gain.value = 0.5;
        gainB.gain.value = 1.0;
        streamA.connect(gainA).connect(merged);
        streamB.connect(gainB).connect(merged);
        return merged.stream.getAudioTracks();
    };
    return {
        mediaStream,
        videoStream,
        resetStream,
        resetDesktopStream,
        audioInputDeviceId,
        videoInputDeviceId,
        getDesktopStream,
        muted,
        closeStreams
    };
};
